'use client';

import React, { Children } from 'react';
import { cn, Alert, AlertDescription, AlertTitle } from 'ui';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCircleCheck,
  IconInfoCircle,
  IconCircle,
} from '@tabler/icons-react';

export type AlertVariant =
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'neutral'
  | 'muted';

type AlertComponentProps = {
  title: React.ReactNode;
  description?: React.ReactNode;
  variant?: AlertVariant;
  hideIcon?: boolean;
  // Custom icon that overrides the default variant icon. Rendered as-is (the
  // alert's color is inherited by any child svg via the variant className).
  icon?: React.ReactNode;
  className?: string;
  iconClassName?: string;
  children?: React.ReactNode;
};

const alertVariants: Record<
  AlertVariant,
  {
    className: string;
    icon: React.ElementType;
  }
> = {
  success: {
    className:
      'border-green-800/60 bg-green-100/50 text-green-800 [&>svg]:text-green-800',
    icon: IconCircleCheck,
  },
  warning: {
    className:
      'border-yellow-800/60 bg-yellow-100/50 text-yellow-800 [&>svg]:text-yellow-800',
    icon: IconAlertTriangle,
  },
  danger: {
    className:
      'border-red-800/60 bg-red-100/50 text-red-800 [&>svg]:text-red-800',
    icon: IconAlertCircle,
  },
  info: {
    className:
      'border-sky-800/60 bg-sky-100/50 text-sky-800 [&>svg]:text-sky-800',
    icon: IconInfoCircle,
  },
  muted: {
    className:
      'border-muted-foreground/40 bg-muted-foreground/10 text-muted-foreground [&>svg]:text-muted-foreground',
    icon: IconInfoCircle,
  },
  neutral: {
    className:
      'border-slate-800/60 bg-slate-100/50 text-slate-800 [&>svg]:text-slate-800',
    icon: IconCircle,
  },
};

export const AlertComponent = ({
  title,
  description,
  variant = 'neutral',
  hideIcon = false,
  icon,
  className,
  iconClassName,
  children,
}: AlertComponentProps) => {
  const { icon: Icon, className: variantClassName } = alertVariants[variant];

  return (
    <Alert className={cn('pb-2.5 rounded-xl', variantClassName, className)}>
      {!hideIcon && (icon ?? <Icon className={cn('h-5 w-5', iconClassName)} />)}

      <div className='flex flex-col gap-2 lg:flex-row'>
        <div className='flex-1 mt-1'>
          <AlertTitle>{title}</AlertTitle>

          {description && <AlertDescription>{description}</AlertDescription>}
        </div>

        {children && children}
      </div>
    </Alert>
  );
};
